import { useEffect, useRef, useState } from 'react'
import { getOrgLiveStatus } from '../services/actions/orgActions'

const REFRESH_INTERVAL_MS = 30_000
const REQUEST_TIMEOUT_MS = 12_000

export default function useOrgLiveStatus(orgSlug) {
	const [data, setData] = useState(null)
	const [isOnline, setIsOnline] = useState(() => navigator.onLine !== false)
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [hasError, setHasError] = useState(false)
	const [updatedAt, setUpdatedAt] = useState(null)
	const [now, setNow] = useState(Date.now())
	const refreshRef = useRef(() => {})

	useEffect(() => {
		let disposed = false
		let controller = null
		let inFlight = false
		let timeoutId = null
		setData(null)
		setUpdatedAt(null)
		setHasError(false)

		const refresh = async () => {
			if (disposed || inFlight || document.hidden || navigator.onLine === false) return
			inFlight = true
			controller = new AbortController()
			setIsRefreshing(true)
			timeoutId = setTimeout(() => controller?.abort(), REQUEST_TIMEOUT_MS)
			try {
				const payload = await getOrgLiveStatus(orgSlug, { signal: controller.signal })
				if (!Array.isArray(payload?.trips) || !payload?.totals) {
					throw new Error('Invalid live status response')
				}
				if (!disposed) {
					setData(payload)
					setUpdatedAt(Date.now())
					setNow(Date.now())
					setHasError(false)
				}
			} catch (error) {
				if (!disposed) setHasError(true)
			} finally {
				clearTimeout(timeoutId)
				inFlight = false
				if (!disposed) setIsRefreshing(false)
			}
		}
		refreshRef.current = refresh
		const handleConnection = () => {
			setIsOnline(navigator.onLine !== false)
			refresh()
		}
		const handleVisibility = () => {
			if (!document.hidden) {
				setNow(Date.now())
				refresh()
			}
		}
		refresh()
		const pollId = setInterval(refresh, REFRESH_INTERVAL_MS)
		const clockId = setInterval(() => {
			if (!document.hidden) setNow(Date.now())
		}, 10_000)
		window.addEventListener('online', handleConnection)
		window.addEventListener('offline', handleConnection)
		document.addEventListener('visibilitychange', handleVisibility)
		return () => {
			disposed = true
			controller?.abort()
			clearTimeout(timeoutId)
			clearInterval(pollId)
			clearInterval(clockId)
			window.removeEventListener('online', handleConnection)
			window.removeEventListener('offline', handleConnection)
			document.removeEventListener('visibilitychange', handleVisibility)
		}
	}, [orgSlug])

	const ageSeconds = updatedAt ? Math.max(0, Math.floor((now - updatedAt) / 1000)) : null
	return {
		data, isOnline, isRefreshing, hasError, updatedAt, ageSeconds,
		isStale: ageSeconds !== null && ageSeconds >= 90,
		refresh: () => refreshRef.current(),
	}
}
